'use client';

import React, { useMemo, useState } from 'react';
import {Button, Card, MultiStepModalWizard, Heading} from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import { Section, Container } from '@layout';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';
import { t as translate } from '@utils/translate';

interface MultiStepModalWizardDemoProps {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  isSpanish?: boolean;
}

type LifecycleItem = {
  id: string;
  panelId: string;
  title: string;
  content: string;
  defaultOpen?: boolean;
};

function isLifecycleItem(value: unknown): value is LifecycleItem {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.panelId === 'string' &&
    typeof v.title === 'string' &&
    typeof v.content === 'string'
  );
}

const MultiStepModalWizardDemo: React.FC<MultiStepModalWizardDemoProps> = ({
  isSpanish = false,
  sectionID = 'wizard',
  classes = '',
  containerId,
  containerClasses,
}) => {
  const [open, setOpen] = useState(false);
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const locale = isSpanish ? 'es' : 'en';
  const wizardStepLabels = useMemo((): [string, string, string] | undefined => {
    const raw =
      (translate('development_process.lifecycle_items', 'design-system', locale) as unknown) ??
      (translate('accordion_demo.lifecycle_items', 'design-system', locale) as unknown);
    const arr = Array.isArray(raw) ? raw : [];
    const items = arr.filter(isLifecycleItem);
    const slice = items.slice(0, 3);
    if (slice.length < 3) return undefined;
    return [slice[0].title, slice[1].title, slice[2].title];
  }, [locale]);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading as="h3">{t('Multi-step Modal Wizard', 'Asistente de modal de varios pasos')}</Heading>
        <p className='sub'>
          {t(
            'Guided flow with validation per step. Reuses your modal system.',
            'Flujo guiado con validacion por paso. Reutiliza tu sistema de modales.'
          )}
        </p>
        <Card>
          <Button
            label={t('Launch Wizard', 'Iniciar asistente')}
            className='btn-secondary'
            onClick={() => setOpen(true)}
          />
        </Card>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<div class="card modal-window size-md">
  <header class="modal-header"><h3 class="modal-title">Step 1 of 3</h3></header>
  <div class="modal-body"><p>Choose a starting point.</p></div>
  <footer class="modal-footer">
    <button class="btn btn-ghost" type="button">Back</button>
    <button class="btn btn-primary" type="button">Next</button>
  </footer>
</div>`}
        />
      </Container>
      <MultiStepModalWizard
        open={open}
        onClose={() => setOpen(false)}
        stepLabels={wizardStepLabels}
      />
    </Section>
  );
};

export default MultiStepModalWizardDemo;
